import { motion } from 'framer-motion';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface ConsultingProps {
  language: 'de' | 'ru';
}

export default function Consulting({ language }: ConsultingProps) {
  const headerAnim = useScrollAnimation();
  const cardsAnim = useScrollAnimation();
  const valuesAnim = useScrollAnimation();

  const content = {
    de: {
      title: 'Brücken bauen & Projekte fördern',
      subtitle: 'Expertise mit Herz und Weitblick',
      card1: {
        number: '70+',
        text: 'Jahre Projekterfahrung',
        slogan: 'Weisheit trifft Innovation'
      },
      card2: {
        number: '100+',
        text: 'Geförderte Initiativen',
        slogan: 'Für eine bessere Zukunft'
      },
      values: 'Werte',
      valuesList: ['Integrität', 'Strategische Weitsicht', 'Generationen-Verantwortung', 'Menschlichkeit'],
      cta: 'Beratung anfragen'
    },
    ru: {
      title: 'Строительство мостов и поддержка проектов',
      subtitle: 'Экспертиза с сердцем и дальновидностью',
      card1: {
        number: '70+',
        text: 'Лет опыта в проектах',
        slogan: 'Мудрость встречает инновации'
      },
      card2: {
        number: '100+',
        text: 'Поддержанных инициатив',
        slogan: 'Для лучшего будущего'
      },
      values: 'Ценности',
      valuesList: ['Честность', 'Стратегическая дальновидность', 'Ответственность поколений', 'Человечность'],
      cta: 'Запросить консультацию'
    }
  };

  const t = content[language];

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariant = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  return (
    <section id="consulting" className="py-24 bg-gradient-to-b from-[#F5F5DC] to-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          ref={headerAnim.ref}
          initial="hidden"
          animate={headerAnim.isVisible ? "visible" : "hidden"}
          variants={fadeInUp}
          className="text-center mb-16"
        >
          <h2 className="font-serif text-5xl md:text-6xl font-bold text-[#1e3a5f] mb-4">
            {t.title}
          </h2>
          <p className="font-serif text-xl text-gray-600">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Decorative Line */}
        <div className="h-px bg-gradient-to-r from-transparent via-[#C5B358] to-transparent mb-16"></div>

        {/* Cards Grid */}
        <motion.div 
          ref={cardsAnim.ref}
          initial="hidden"
          animate={cardsAnim.isVisible ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16"
        >
          {/* Card 1 - Humanitarian Projects */}
          <motion.div 
            variants={cardVariant}
            className="relative bg-[#8b2332]/15 rounded-lg p-12 overflow-hidden group hover:shadow-xl transition-all duration-300"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#C5B358]/10 rounded-full -mr-16 -mt-16"></div>
            
            <div className="relative z-10">
              <div className="flex justify-between items-start mb-8">
                <div>
                  <div className="font-serif text-7xl font-bold text-[#C5B358] mb-2">
                    {t.card1.number}
                  </div>
                  <div className="font-serif text-xl text-[#1e3a5f]">
                    {t.card1.text}
                  </div>
                </div>
                <div className="w-12 h-12 rounded-full bg-white border-2 border-[#C5B358] flex items-center justify-center cursor-pointer hover:bg-[#C5B358] transition-colors group">
                  <i className="ri-arrow-right-up-line text-xl text-[#C5B358] group-hover:text-white"></i>
                </div>
              </div>
              
              <p className="font-serif italic text-lg text-[#1e3a5f]/80">
                {t.card1.slogan}
              </p>
            </div>
          </motion.div>

          {/* Card 2 - Funding Expertise */}
          <motion.div 
            variants={cardVariant}
            className="relative bg-[#1e3a5f] rounded-lg p-12 overflow-hidden group hover:shadow-xl transition-all duration-300"
          >
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#C5B358]/10 rounded-full -ml-20 -mb-20"></div>
            
            <div className="relative z-10">
              <div className="flex justify-between items-start mb-8">
                <div>
                  <div className="font-serif text-7xl font-bold text-[#C5B358] mb-2">
                    {t.card2.number}
                  </div>
                  <div className="font-serif text-xl text-white">
                    {t.card2.text}
                  </div>
                </div>
                <div className="w-12 h-12 rounded-full bg-white border-2 border-[#C5B358] flex items-center justify-center cursor-pointer hover:bg-[#C5B358] transition-colors group">
                  <i className="ri-arrow-right-up-line text-xl text-[#C5B358] group-hover:text-white"></i>
                </div>
              </div>
              
              <p className="font-serif italic text-lg text-white/80">
                {t.card2.slogan}
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Values Section */}
        <motion.div 
          ref={valuesAnim.ref}
          initial="hidden"
          animate={valuesAnim.isVisible ? "visible" : "hidden"}
          variants={fadeInUp}
          className="bg-gradient-to-r from-[#F5F5DC] to-[#fefdfb] rounded-lg p-12"
        >
          <h3 className="font-serif text-3xl font-bold text-[#1e3a5f] mb-8 text-center">
            {t.values}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.valuesList.map((value, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className="w-3 h-3 rounded-full bg-[#C5B358]"></div>
                <span className="font-serif text-lg text-gray-700">{value}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA Button */}
        <div className="text-center mt-12">
          <button className="px-12 py-4 bg-[#8b2332] text-white font-serif text-lg rounded-full border-2 border-[#C5B358] hover:bg-[#6d1a27] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer">
            {t.cta}
          </button>
        </div>
      </div>
    </section>
  );
}
